import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyProgressBar,
  SkyText,
  SkyTooltip,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "wouter";
import type { RunEventExportFormat, RunSummaryRow } from "@shared/types/runs";
import { pixelColumnLayout } from "@/lib/grid";
import { usePinnedColumnEdge } from "@/lib/gridPinned";
import { absolutePath } from "@/lib/routes";
import { CopyIcon } from "@/lib/icons";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { queuedBehindWord } from "@/lib/transformations/runOutcome";
import { RunRowMenu } from "./RunRowMenu";
import { partlyFailedDetail } from "@shared/runs/partlyFailed";
import { StatusCell } from "./StatusCell";
import { scopeTableCount, tablesWord } from "./tableRuns";
import {
  NO_TABLES_TRANSFORM_ONLY,
  RUN_TYPE_LABELS,
  STARTED_BY_KIND_LABELS,
  connectionsWhatRan,
  formatDuration,
  NOT_STARTED_LABEL,
  formatRowsLine,
  isNotStarted,
  runPartlyFailedFacts,
  runStatusPresentation,
  transformLineParts,
  transformWhatRan,
  type RowsLinePart,
} from "./runFormatting";
import { formatInstant, instantHoverTitle } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useElapsedSeconds } from "@/hooks/use-elapsed-seconds";

/**
 * Run history (FR-56), the basis of which is Studio's `DataflowRunHistory`
 * with its V1 branch dropped.
 *
 * The row is built for triage: status, the step a running import is on, and
 * how many steps have finished are all in the row, so opening a run is a choice
 * rather than the only way to learn what happened. Colour is never the only
 * signal — every status carries its word — and the step counts are given as
 * "3 of 5 steps done" rather than as a bare percentage, because a percentage of
 * a five-step chain tells a reader less than the count does.
 */

function stepsDone(row: RunSummaryRow): { done: number; total: number } {
  const counts = row.stepCounts ?? {};
  const total = Object.values(counts).reduce((sum, value) => sum + (value ?? 0), 0);
  const done =
    (counts.succeeded ?? 0) +
    (counts.skipped ?? 0) +
    (counts.failed ?? 0) +
    (counts.cancelled ?? 0);
  return { done, total };
}

/** What the Started by cell says when the server could not read who started the run. */
export const STARTED_BY_UNREAD = "Who started this run could not be read.";

/**
 * Who started a run (Session 11, D3): the server's label ("Ada Lovelace", "a***@example.com",
 * "Scheduler · nightly", "System"), and under it, for a Run now of a schedule, which schedule.
 * The label is never built here: the server masks it. With no `startedBy` (the server's users
 * read failed, or an older server) the cell is a dash with a tooltip that says so. Shared by the
 * Runs list, a connection's Runs view, History and a schedule's history, so one run reads the
 * same on each.
 *
 * gridsfit (lead, 2026-09-30): a grid that passes `trigger` (the Runs list, a connection's Runs)
 * has no Trigger column.
 *
 * Round 4 (owner, 2026-09-30, item 10b, as the lead decided): with `trigger` the two lines are
 * symmetric. Line 1 is the kind ("Manual", "Scheduler", `STARTED_BY_KIND_LABELS`); line 2, muted,
 * is who or what: the person, or the schedule's name, from the server's `name` (never sliced
 * out of `label`), and for a person's Run now of a schedule "Ada Lovelace · Run now of Nightly".
 * A firing no longer reads "Scheduler · Run2" over "Scheduled". Line 2 carries its whole text as
 * a `title`, and since round 6 (W22) wraps in a narrow column instead of being cut. No address is ever shown: the server sends
 * none (SEC-17). Without `trigger` (History, a schedule's History) the cell is as before: the
 * label, and the Run now of a schedule under it.
 */
export function StartedByCell({
  row,
  scheduleNames,
  trigger,
}: {
  row: Pick<RunSummaryRow, "runId" | "kind" | "scheduleId" | "startedBy">;
  scheduleNames?: Map<string, string>;
  /** The run's trigger, read as the cell's second line. */
  trigger?: string;
}) {
  const testId = `run-started-by-${row.runId}`;
  const runNowOf =
    row.kind === "on_demand" && row.scheduleId
      ? (scheduleNames?.get(row.scheduleId) ?? "a schedule")
      : null;
  const runNowText = runNowOf ? `Run now of ${runNowOf}` : null;
  if (trigger) return <KindOverName row={row} trigger={trigger} runNowText={runNowText} />;
  const secondary =
    trigger || runNowText ? (
      <SkyText
        size={200}
        color="muted"
        block
        className="break-words"
        title={[trigger, runNowText].filter(Boolean).join(" · ")}
      >
        {trigger ? <span data-testid={`${testId}-trigger`}>{trigger}</span> : null}
        {trigger && runNowText ? " · " : null}
        {runNowText ? <span data-testid={`${testId}-schedule`}>{runNowText}</span> : null}
      </SkyText>
    ) : null;
  if (!row.startedBy) {
    return (
      <div className="flex min-w-0 flex-col">
        {/* The trigger takes focus, so a keyboard user can open the reason too (the Runs view's
            precedent, `ConnectionRunColumns.tsx`), and a screen reader reads it in place. */}
        <SkyTooltip content={STARTED_BY_UNREAD} testId={`${testId}-hint`}>
          <span tabIndex={0} data-testid={testId}>
            <SkyText size={200} color="muted">
              —
            </SkyText>
            <span className="sr-only">{STARTED_BY_UNREAD}</span>
          </span>
        </SkyTooltip>
        {secondary}
      </div>
    );
  }
  return (
    // Round 3 (owner, 2026-09-30: "it's cut ... at least show more details on mouse over"): each
    // line carries its whole text as a `title`. Round 6 W22 (walk: "Started by is cut to
    // 'skyData test platform…'"): the label and the second line wrap instead of truncating.
    <div className="flex min-w-0 flex-col" data-testid={testId}>
      <SkyText size={200} block className="break-words" title={row.startedBy.label}>
        {row.startedBy.label}
      </SkyText>
      {secondary}
    </div>
  );
}

/** The Started by cell's round-4 shape: the kind over who or what (`StartedByCell`). */
function KindOverName({
  row,
  trigger,
  runNowText,
}: {
  row: Pick<RunSummaryRow, "runId" | "startedBy">;
  trigger: string;
  runNowText: string | null;
}) {
  const testId = `run-started-by-${row.runId}`;
  const name = row.startedBy ? (row.startedBy.name ?? row.startedBy.label) : null;
  const kindLine = (
    <SkyText size={200} block truncate title={trigger}>
      <span data-testid={`${testId}-trigger`}>{trigger}</span>
    </SkyText>
  );
  const runNow = runNowText ? (
    <>
      {" · "}
      <span data-testid={`${testId}-schedule`}>{runNowText}</span>
    </>
  ) : null;
  if (name === null) {
    return (
      <div className="flex min-w-0 flex-col">
        {kindLine}
        <SkyText
          size={200}
          color="muted"
          block
          className="break-words"
          title={runNowText ?? undefined}
        >
          {/* The trigger takes focus, so a keyboard user can open the reason too, and a screen
              reader reads it in place. */}
          <SkyTooltip content={STARTED_BY_UNREAD} testId={`${testId}-hint`}>
            <span tabIndex={0} data-testid={testId}>
              —<span className="sr-only">{STARTED_BY_UNREAD}</span>
            </span>
          </SkyTooltip>
          {runNow}
        </SkyText>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col" data-testid={testId}>
      {kindLine}
      {/* Round 6 W22: who or what wraps rather than being cut; the kind above is one word. */}
      <SkyText
        size={200}
        color="muted"
        block
        className="break-words"
        title={[name, runNowText].filter(Boolean).join(" · ")}
      >
        <span data-testid={`${testId}-name`}>{name}</span>
        {runNow}
      </SkyText>
    </div>
  );
}

/**
 * When a run started, and under it how long it took (gridsfit, lead, 2026-09-30): the Runs list,
 * a connection's Runs and History had a Duration column, and ten columns did not fit the pane, so
 * the start and its duration scrolled out of view. One cell holds both: the time on two lines at
 * its comma (128 px) over "took 1m 05s". The header stays "Started" and the column sorts by the
 * start; the duration is read as part of the cell ("took ..."), never as a second header. A
 * duration not known yet (a run still going, a server without it) is a dash, said in words for a
 * screen reader. `durationTestId` is the old Duration cell's id, on the value alone.
 *
 * Round 6 (W6): a run with a dbt step has two durations, the run's and the dbt step's, and the
 * screens showed one each, unnamed ("1m 14s" on Runs, "1m 04s" on Transformations). With
 * `dbtStep` and both known, the cell names both: "Run 1m 14s" over "dbt step 1m 04s".
 *
 * #1201: a run still going passes `runningSince`, its start, and with no duration yet the cell
 * reads how long it has been going, "running for 5m 12s", ticking each second, never a bare dash.
 */
export function StartedCell({
  at,
  durationSeconds,
  durationTestId,
  dbtStep,
  runningSince,
}: {
  at: string | null | undefined;
  durationSeconds: number | null | undefined;
  durationTestId: string;
  dbtStep?: { seconds: number | null | undefined; testId: string };
  runningSince?: string | null;
}) {
  const zone = useDisplayTz();
  const known = isKnownSeconds(durationSeconds);
  const elapsed = useElapsedSeconds(known ? null : runningSince);
  if (known && dbtStep && isKnownSeconds(dbtStep.seconds)) {
    return (
      <div className="flex min-w-0 flex-col">
        <SkyText size={200} block title={instantHoverTitle(at, zone)}>
          {formatInstant(at, zone, "seconds")}
        </SkyText>
        <SkyText size={200} color="muted" font="numeric" block>
          {"Run "}
          <span data-testid={durationTestId}>{formatDuration(durationSeconds)}</span>
        </SkyText>
        <SkyText size={200} color="muted" font="numeric" block>
          {"dbt step "}
          <span data-testid={dbtStep.testId}>{formatDuration(dbtStep.seconds)}</span>
        </SkyText>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col">
      <SkyText size={200} block title={instantHoverTitle(at, zone)}>
        {formatInstant(at, zone, "seconds")}
      </SkyText>
      <SkyText size={200} color="muted" font="numeric" block>
        {known ? (
          <>
            {"took "}
            <span data-testid={durationTestId}>{formatDuration(durationSeconds)}</span>
          </>
        ) : elapsed !== null ? (
          <>
            {"running for "}
            <span data-testid={durationTestId}>{formatDuration(elapsed)}</span>
          </>
        ) : (
          <>
            <span className="sr-only">Duration not known</span>
            <span aria-hidden="true" data-testid={durationTestId}>
              —
            </span>
          </>
        )}
      </SkyText>
    </div>
  );
}

/** A duration in seconds the server reported (not null, not missing, finite). */
export function isKnownSeconds(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value);
}

export interface RunsTableProps {
  rows: RunSummaryRow[];
  isLoading: boolean;
  canCancel: boolean;
  onCancel: (row: RunSummaryRow) => void;
  connectionNames: Map<string, string>;
  /** Schedule names by id, for "Run now of {schedule}" under a manual run's Started by. */
  scheduleNames?: Map<string, string>;
  /** `runs.retry`: the menu's "Retry from failed step" (it opens the run page's dialog). */
  canRetry?: boolean;
  onRetry?: (row: RunSummaryRow) => void;
  /** The menu's two exports; every role that reads runs may make them. */
  onExport?: (row: RunSummaryRow, format: RunEventExportFormat) => void;
  /** The empty grid's line; the page passes "No runs match these filters." while one applies. */
  emptyMessage?: string;
}

/**
 * Round 3 (owner, 2026-09-30, BmlBLH.png): with ten percentage columns "Started" and "Duration"
 * ran into each other, run ids wrapped to three lines and a warning pill ran out of Status. A
 * header's width is a fact of the font (client/AGENTS.md), so the columns a value sizes have pixel
 * widths, measured in Chromium in the app's stack (DejaVu Sans on a Linux browser, the widest) and
 * in Inter, border-box with the cell's 24 px padding: the run id holds its first 24 characters
 * (173 px at 12 px monospace) and so is two lines at most; Status held "Succeeded with warning"
 * (131 px; since #817 the pill reads "Partly failed", what failed wrapping under it); Started holds its sortable header (122 px), its time on two lines at the comma
 * ("Sep 29, 2026," 84 px) and "took 59m 59s" (84 px); Actions holds its header (99 px).
 *
 * gridsfit (lead, 2026-09-30): ten real headers needed a 1336 px floor, and at 1440 px Started and
 * Duration, the columns the owner could not read, scrolled out of the 1131 px pane. Facts that
 * belong together share a cell instead: the start over its duration (`StartedCell`), the person
 * over the trigger (`StartedByCell`), What ran over its Type pills. The rest share what is left
 * in proportion to a minimum: What ran holds the widest pill ("Import + transform", 107 px) and a
 * name like "Mouseflow sample site" (160 px at 14 px); Started by and Rows their headers and
 * values. The floor, 1040 px, fits the pane at 1440 px. At 1280 px (971 px) the run, what ran, its
 * status, its start and who started it are in view; Rows scrolls under the pinned Actions.
 *
 * Round 10 (I1): at 1280 px the 1040 px table sat in a 978 px container and Rows was clipped
 * ("1,000 ext...") behind a scroll nobody saw. The run id's short form and its copy button need
 * 160 px (not 208), What ran 168 (its widest pill is 107 px and a name wraps) and Started by 128,
 * so the floor is 968 px and every column is readable at 1280 px with no sideways scroll. Below
 * that (the 1024 px tablet floor) the grid scrolls inside its region with Actions pinned, as the
 * Connections and Schedules lists do.
 *
 * #1082 (QA, 10/14: at 1024 px "Started by" read "Tenant Ac" and Rows was out of view): the 968 px
 * floor was wider than the 927 px pane at 1024 px (`PANE_WIDTH.at1024`), so Rows scrolled under the
 * pinned Actions. The floor is now 920 px, inside it, so every column is in view beside Actions
 * at 1024 px (the nav rail) and from 1229 px with the 260 px drawer open (floor plus 309; between
 * them Started by and Rows scroll under Actions, pinned in the layout test): the run id's short form and copy button need 150 px (152), What ran its widest
 * pill 107 plus padding (136, a name wraps), Rows its header 48 plus 32 (112) and Actions its
 * header (100: View and the menu are 60 px). Below the floor the others still scroll under it.
 */
const RUN_ID_WIDTH = 152;
const STATUS_WIDTH = 160;
const STARTED_WIDTH = 128;
const ACTIONS_WIDTH = 100;
const MIN_COLUMN_WIDTH: Readonly<Record<string, number>> = {
  dataflowIds: 136,
  startedBy: 128,
  totals: 112,
};

/**
 * Round 6, W22: "0db0c32a…4fe9", the run id's first part and its last four characters. The whole
 * id is a run's composite key (`{hex8}-{yyyymmddhhmmss}-{hex8}`), 33 characters that wrapped onto
 * two lines in every row; the short form tells rows apart, and the whole one is copied, read to a
 * screen reader and found by the browser's find.
 */
function shortRunId(runId: string): string {
  if (runId.length <= 14) return runId;
  const head = runId.split("-")[0] ?? runId.slice(0, 8);
  return `${head.slice(0, 8)}…${runId.slice(-4)}`;
}

/** The Run cell: the short id as the link, and a copy button for the whole id. */
function RunIdCell({ runId }: { runId: string }) {
  const notice = useNotice();
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(runId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused in some browsers and every insecure origin; the run page's
      // header shows the whole id to select by hand.
      notice.warning("The run id could not be copied. Open the run to select it by hand.");
    }
  };
  return (
    <div className="flex items-center gap-1" data-testid={`run-id-${runId}`}>
      <Link
        href={absolutePath(`/runs/${runId}`)}
        className="underline-offset-2 hover:underline"
        aria-label={runId}
      >
        <SkyText size={200} font="monospace" weight="semibold" color="accent" title={runId}>
          <span aria-hidden="true">{shortRunId(runId)}</span>
          <span className="sr-only">{runId}</span>
        </SkyText>
      </Link>
      <SkyTooltip content={copied ? "Copied" : "Copy run id"}>
        <SkyButton
          appearance="subtle"
          size="small"
          icon={CopyIcon}
          aria-label={`Copy run id ${runId}`}
          onClick={() => void copy()}
          testId={`run-copy-${runId}`}
        />
      </SkyTooltip>
    </div>
  );
}

/** One line of parts, a failure part in the destructive token (never colour alone: it says "failed"). */
function LineParts({ parts, testId }: { parts: RowsLinePart[]; testId?: string }) {
  return (
    <SkyText size={200} font="numeric" block testId={testId}>
      {parts.map((part, index) => (
        <span
          key={`${index}-${part.text}`}
          className={part.tone === "danger" ? "text-destructive" : undefined}
        >
          {index > 0 ? " · " : ""}
          {part.text}
        </span>
      ))}
    </SkyText>
  );
}

function rowsCell(row: RunSummaryRow) {
  const testId = `run-rows-${row.runId}`;
  if (row.type === "transform") {
    const parts = transformLineParts(row.transformTotals);
    return (
      <LineParts
        parts={parts.length > 0 ? parts : [{ text: NO_TABLES_TRANSFORM_ONLY, tone: "default" }]}
        testId={testId}
      />
    );
  }
  if (row.type === "import_transform") {
    // The transform step exists from the start with no counts: while it is queued or running,
    // or skipped after a failed extract or load, there is no second line (never "No tables").
    const parts = transformLineParts(row.transformTotals);
    return (
      <div className="flex flex-col">
        <SkyText size={200} font="numeric" block testId={testId}>
          {formatRowsLine(row.totals, { short: true })}
        </SkyText>
        {parts.length > 0 ? (
          <LineParts parts={parts} testId={`run-transform-${row.runId}`} />
        ) : null}
      </div>
    );
  }
  // An import, or a row from an older server: the short rows line, a dash with no tables.
  return (
    <SkyText size={200} font="numeric" block testId={testId}>
      {formatRowsLine(row.totals, { short: true })}
    </SkyText>
  );
}

function whatRanCell(row: RunSummaryRow, connectionNames: Map<string, string>) {
  const connections = connectionsWhatRan(row.dataflowIds, connectionNames);
  const transform =
    row.type === "transform" || row.type === "import_transform"
      ? transformWhatRan(row.dbtJobNames)
      : null;
  if (!connections && !transform) {
    return (
      <SkyText size={300} color="muted">
        No connection
      </SkyText>
    );
  }
  return (
    <div className="flex min-w-0 flex-col" data-testid={`run-what-${row.runId}`}>
      {/* Round 6, W22: a long name wraps rather than being cut. */}
      {connections ? (
        <SkyText size={300} block className="break-words" title={connections}>
          {connections}
        </SkyText>
      ) : null}
      {transform ? (
        <SkyText
          size={connections ? 200 : 300}
          color={connections ? "muted" : undefined}
          block
          className="break-words"
          title={transform}
        >
          {transform}
        </SkyText>
      ) : null}
    </div>
  );
}

/**
 * The run's Type pills (Session 10 P7 full refresh; product gaps row 2, the tables a partial run
 * covered), under What ran since gridsfit: there is no Type column. Nothing when none applies.
 */
function typeBadges(row: RunSummaryRow) {
  return row.type || row.fullRefresh === true || scopeTableCount(row.scope) !== null ? (
    <div className="flex flex-wrap items-center gap-1">
      {row.type ? (
        <SkyBadge appearance="outline" color="subtle" testId={`run-type-${row.runId}`}>
          {RUN_TYPE_LABELS[row.type]}
        </SkyBadge>
      ) : null}
      {/* Session 10 (P7): only when the run says so; absent or false shows nothing. */}
      {row.fullRefresh === true ? (
        <SkyBadge appearance="tint" color="informative" testId={`run-full-refresh-${row.runId}`}>
          Full refresh
        </SkyBadge>
      ) : null}
      {/* Product gaps row 2: a run that covered only some tables says how many. */}
      {scopeTableCount(row.scope) !== null ? (
        <SkyBadge appearance="tint" color="informative" testId={`run-scope-tables-${row.runId}`}>
          {tablesWord(scopeTableCount(row.scope) as number)}
        </SkyBadge>
      ) : null}
    </div>
  ) : null;
}

/**
 * Under a run's status: the dbt run its transform waits behind (round 6, T1), else, while it
 * moves, its steps done; nothing for a finished run.
 */
function statusSecondary(
  row: RunSummaryRow,
  isActive: boolean,
  done: number,
  total: number,
  connectionNames: Map<string, string>,
): ReactNode {
  if (row.status === "partially_succeeded") {
    // #817: what failed, under the "Partly failed" badge: "1 of 2 connections failed (HR)".
    return (
      <SkyText
        size={200}
        color="muted"
        block
        className="break-words"
        testId={`run-partly-failed-${row.runId}`}
      >
        {partlyFailedDetail(runPartlyFailedFacts(row, connectionNames))}
      </SkyText>
    );
  }
  if (row.transformQueuedBehind) {
    return (
      <SkyText size={200} color="muted" testId={`run-queued-behind-${row.runId}`}>
        {queuedBehindWord(row.transformQueuedBehind)}
      </SkyText>
    );
  }
  if (isActive && total > 0) {
    return (
      <>
        <SkyProgressBar
          value={(done / total) * 100}
          thickness="small"
          label={`${row.runId}: ${done} of ${total} steps done`}
          className="w-28 max-w-full"
        />
        <SkyText size={200} color="muted">
          {done} of {total} steps done
        </SkyText>
      </>
    );
  }
  return undefined;
}

export function RunsTable({
  rows,
  isLoading,
  canCancel,
  onCancel,
  connectionNames,
  scheduleNames,
  canRetry = false,
  onRetry = () => undefined,
  onExport = () => undefined,
  emptyMessage,
}: RunsTableProps) {
  const pinnedEdge = usePinnedColumnEdge<HTMLDivElement>(true);
  const columns: SkyDataGridColumn<RunSummaryRow>[] = [
    {
      key: "runId",
      header: "Run",
      width: `${RUN_ID_WIDTH}px`,
      // Round 6, W22: one line, the short id and a copy button (the whole id on hover).
      renderCell: (_value, row) => <RunIdCell runId={row.runId} />,
    },
    {
      // gridsfit: What ran over its Type pills (there is no Type column).
      key: "dataflowIds",
      header: "What ran",
      width: "13%",
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col gap-1">
          {whatRanCell(row, connectionNames)}
          {typeBadges(row)}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: `${STATUS_WIDTH}px`,
      renderCell: (_value, row) => {
        const presentation = runStatusPresentation(row.status);
        const { done, total } = stepsDone(row);
        // One line for a finished run; the steps only while it moves (owner, 2026-09-28).
        return (
          <StatusCell
            testId={`run-status-${row.runId}`}
            badge={
              <SkyBadge appearance="tint" color={presentation.color}>
                {presentation.label}
              </SkyBadge>
            }
            secondary={statusSecondary(row, presentation.isActive, done, total, connectionNames)}
          />
        );
      },
    },
    {
      // gridsfit: the start over its duration (`StartedCell`); the column sorts by the start.
      key: "createdAt",
      header: "Started",
      width: `${STARTED_WIDTH}px`,
      sortable: true,
      renderCell: (_value, row) =>
        // #986: a queued run has not started, so the creation time is not a start time.
        isNotStarted(row) ? (
          <SkyText size={200} color="muted" block testId={`run-not-started-${row.runId}`}>
            {NOT_STARTED_LABEL}
          </SkyText>
        ) : (
          <StartedCell
            at={row.startedAt ?? row.createdAt}
            durationSeconds={row.durationSeconds}
            durationTestId={`run-duration-${row.runId}`}
            runningSince={row.status === "running" ? row.startedAt : null}
            dbtStep={
              row.transformDurationSeconds !== undefined
                ? { seconds: row.transformDurationSeconds, testId: `run-dbt-duration-${row.runId}` }
                : undefined
            }
          />
        ),
    },
    {
      // Session 11 (D3): who started it, as the server resolved it; round 4 (10b): the kind over it.
      key: "startedBy",
      header: "Started by",
      width: "11%",
      renderCell: (_value, row) => (
        <StartedByCell
          row={row}
          scheduleNames={scheduleNames}
          trigger={STARTED_BY_KIND_LABELS[row.kind] ?? row.kind}
        />
      ),
    },
    {
      // Rows per table (O1), and for a transform the models and tests it reported (S10): the
      // short form of the run page's sentence. A run nobody reported on reads as a dash.
      key: "totals",
      header: "Rows",
      width: "13%",
      renderCell: (_value, row) => rowsCell(row),
    },
    {
      // Never empty (S10): View on every row, and a menu whose two exports every reader may
      // use. Retry and Cancel are in the menu only with their permission and when they apply.
      key: "actions",
      header: "Actions",
      width: `${ACTIONS_WIDTH}px`,
      align: "right",
      renderCell: (_value, row) => (
        <div className="flex items-center justify-end gap-1">
          <Link
            href={absolutePath(`/runs/${row.runId}`)}
            aria-label={`View run ${row.runId}`}
            className="underline-offset-2 hover:underline"
            data-testid={`run-view-${row.runId}`}
          >
            <SkyText size={200} color="accent">
              View
            </SkyText>
          </Link>
          <RunRowMenu
            row={row}
            canRetry={canRetry}
            canCancel={canCancel}
            onExport={onExport}
            onRetry={onRetry}
            onCancel={onCancel}
          />
        </div>
      ),
    },
  ];

  const layout = pixelColumnLayout(columns, { minimums: MIN_COLUMN_WIDTH, selection: false });
  return (
    <div ref={pinnedEdge} style={{ "--runs-grid-floor": `${layout.floor}px` } as CSSProperties}>
      <SkyDataGrid<RunSummaryRow>
        data={rows}
        columns={layout.columns}
        getRowId={(row) => row.runId}
        loading={isLoading}
        emptyMessage={emptyMessage ?? "No runs yet"}
        className="grid-pinned-last [&_table]:min-w-(--runs-grid-floor)"
        testId="runs-grid"
      />
    </div>
  );
}
